import { GridCellProps } from '@progress/kendo-react-grid';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { useQueryClient } from '@tanstack/react-query';
import { trim, trimStart } from 'lodash';
import { SyntheticEvent, useCallback, useContext, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import GridCellBase from 'src/components/GridCellBase';
import { queryKeys } from 'src/constants/querykeys';
import { EditCellContext } from 'src/contexts/EditCellContext';
import useCompany from 'src/hooks/useCompany';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useAddDepartmentMutation from 'src/services/department/useAddDepartmentMutation';

const RenameDepartmentCell = (props: GridCellProps) => {
  const queryClient = useQueryClient();
  const [cellClick] = useContext(EditCellContext);
  const { latestClickedId, setLatestClickedId } = cellClick?.(null);
  const { dataItem, onChange, dataIndex } = props;
  const { rowId, isNewDepartmentName, departmentId, editedName = '' } = dataItem;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const value = trim(dataItem.editedName);

  const openEdit = useCallback(() => {
    setLatestClickedId(rowId);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [rowId, setLatestClickedId]);

  const closeEdit = useCallback(() => setLatestClickedId(0), [setLatestClickedId]);
  const isEditable = rowId === latestClickedId;
  const { hasEditDepartmentPermission, departments, setDepartmentTableData } = useViewDepartment();

  const {
    company: { companyId },
  } = useCompany();

  const onSuccess = () => {
    if (isNewDepartmentName) {
      toast.success('Department created successfully');
    } else {
      toast.success('Department name updated successfully');
    }
  };
  const onError = (error: any) => {
    toast.error(error);
    if (!isNewDepartmentName) {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.department.departmentList(companyId.toString())],
      });
      // Invalidate clients query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.company.clients(companyId.toString())],
      });
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  const { mutateAsync: addNewDepartmentAsync } = useAddDepartmentMutation({
    companyId: Number(companyId),
    onSuccess,
    onError,
  });

  const handleBlur = useCallback(async () => {
    const originalValue = departments?.find((department) => department.name === value);
    if (!value) {
      toast.error('Please provide valid department name');
      setDepartmentTableData((prev) => prev.filter((item) => !item.isNewDepartmentName));
      return;
    } else if (value === originalValue?.name && departmentId !== originalValue?.departmentId) {
      toast.error('Department name must be unique');
      return;
    } else if (
      isNewDepartmentName ||
      (value !== originalValue?.name && departmentId !== originalValue?.departmentId)
    ) {
      await addNewDepartmentAsync(
        isNewDepartmentName ? [{ name: value }] : [{ name: value, departmentId: departmentId }]
      );
    }

    closeEdit();
  }, [
    addNewDepartmentAsync,
    closeEdit,
    departmentId,
    departments,
    isNewDepartmentName,
    setDepartmentTableData,
    value,
  ]);

  const handleChange = useCallback(
    (e: InputChangeEvent) => {
      onChange?.({
        dataIndex: dataIndex,
        dataItem: dataItem,
        field: 'editedName',
        syntheticEvent: e as unknown as SyntheticEvent,
        value: trimStart(String(e.value)),
      });
    },
    [dataIndex, dataItem, onChange]
  );

  useEffect(() => {
    if (inputRef?.current || isNewDepartmentName) {
      inputRef?.current?.focus();
    }
  }, [isNewDepartmentName]);

  return (
    <GridCellBase
      onClick={
        hasEditDepartmentPermission && (isNewDepartmentName || !isEditable) ? openEdit : undefined
      }
      title={hasEditDepartmentPermission ? 'Click to Edit' : undefined}
      {...props}
    >
      {hasEditDepartmentPermission && (isNewDepartmentName || isEditable) ? (
        <>
          <Input
            value={trimStart(editedName)}
            onBlur={handleBlur}
            onChange={handleChange}
            ref={inputRef}
          />
        </>
      ) : (
        <span>{editedName}</span>
      )}
    </GridCellBase>
  );
};

export default RenameDepartmentCell;
